import React from 'react';
import { Modal, Button } from 'components';
import { ReportsService } from 'services';
import moment from 'moment';

interface Props {
	visible: boolean;
	parentUserId?: number | null; // si viene, filtra; si no, muestra todos
	parentName?: string;          // para el título
	onClose: () => void;
}

interface SubuserRow {
	id?: number | string;
	name?: string;
	lastname?: string;
	email?: string;
	created_at?: string;
	has_patrimony?: boolean;
	has_active_plan?: boolean;
	plan_name?: string;
	parent_user_id?: number | null;
	parent_name?: string;
	parent_lastname?: string;
}

interface State {
	loading: boolean;
	subusers: SubuserRow[];
	count: number;
	error: boolean;
}

class ModalSubusersDetail extends React.Component<Props, State> {

	state: State = {
		loading: false,
		subusers: [],
		count: 0,
		error: false,
	}

	componentDidUpdate(prevProps: Props) {
		const opened = this.props.visible && !prevProps.visible;
		const changed = this.props.visible &&
			prevProps.parentUserId !== this.props.parentUserId;
		if (opened || changed) {
			this.load();
		}
	}

	load = async () => {
		this.setState({ loading: true, error: false });
		try {
			const res: any = await ReportsService.admin.getSubusersDetail({
				parent_user_id: this.props.parentUserId || undefined,
				withoutLoading: true,
			}) || {};
			const subusers = Array.isArray(res.subusers)
				? res.subusers
				: Array.isArray(res.users) ? res.users : [];
			this.setState({
				subusers,
				count: typeof res.count === 'number' ? res.count : subusers.length,
				loading: false,
			});
		}
		catch (e) {
			this.setState({ loading: false, error: true, subusers: [], count: 0 });
		}
	}

	fullName = (u: SubuserRow) => {
		const name = [u?.name, u?.lastname].filter(Boolean).join(' ').trim();
		return name || '—';
	}

	parentLabel = (u: SubuserRow) => {
		const name = [u?.parent_name, u?.parent_lastname].filter(Boolean).join(' ').trim();
		return name || (u?.parent_user_id != null ? `#${ u.parent_user_id }` : '—');
	}

	exportCSV = () => {
		const { subusers } = this.state;
		if (!subusers.length) return;

		const header = [
			'ID', 'Nombre', 'Apellido', 'Email', 'Registro',
			'Padre ID', 'Padre Nombre', 'Tiene Patrimonio', 'Plan Activo', 'Plan'
		];
		const rows = subusers.map((u) => ([
			u?.id ?? '',
			u?.name || '',
			u?.lastname || '',
			u?.email || '',
			u?.created_at ? moment(u.created_at).format('DD/MM/YYYY') : '',
			u?.parent_user_id ?? '',
			[u?.parent_name, u?.parent_lastname].filter(Boolean).join(' ').trim(),
			u?.has_patrimony ? 'Sí' : 'No',
			u?.has_active_plan ? 'Sí' : 'No',
			u?.has_active_plan ? (u?.plan_name || 'Premium') : 'Free',
		]));

		const escape = (v: any) => {
			const s = String(v ?? '');
			return /[",\n]/.test(s) ? `"${ s.replace(/"/g, '""') }"` : s;
		}
		const csv = [header, ...rows]
			.map((r) => r.map(escape).join(','))
			.join('\n');

		const blob = new Blob([`﻿${ csv }`], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		const suffix = this.props.parentUserId ? `padre-${ this.props.parentUserId }` : 'todos';
		a.download = `subusuarios-${ suffix }-${ moment().format('YYYYMMDD-HHmm') }.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	render() {
		const { visible, parentUserId, parentName, onClose } = this.props;
		const { loading, subusers, count, error } = this.state;

		const filtered = parentUserId != null;
		const title = filtered
			? `Subusuarios de ${ (parentName || '').trim() || `#${ parentUserId }` }`
			: 'Todos los subusuarios';

		// columnas dinámicas: "Padre" solo cuando se muestran TODOS
		const colCount = filtered ? 5 : 6;

		return (
			<Modal
				className="modal-subusers-detail"
				visible={ visible }
				title={ title }
				onClose={ onClose }
			>
				<div id="modal-subusers-detail">
					<div className="msd-summary">
						{
							loading ? (
								<span className="msd-summary-skeleton" />
							) : error ? (
								<span className="msd-error">No se pudieron cargar los subusuarios</span>
							) : (
								<span>
									<strong>{ count }</strong> { count === 1 ? 'subusuario encontrado' : 'subusuarios encontrados' }
								</span>
							)
						}
					</div>

					<div className="msd-table-wrap">
						<table className="table">
							<thead>
								<tr>
									<th>Nombre</th>
									<th>Email</th>
									<th>Registro</th>
									<th className="text-center">Patrimonio</th>
									<th className="text-center">Plan</th>
									{ !filtered && <th>Padre</th> }
								</tr>
							</thead>
							<tbody>
								{
									loading && (
										[0, 1, 2, 3, 4].map((i) => (
											<tr key={ `sk-${ i }` } className="msd-skeleton-row">
												{ Array.from({ length: colCount }).map((_x, c) => (
													<td key={ c }><span className="msd-cell-skeleton" /></td>
												)) }
											</tr>
										))
									)
								}
								{
									!loading && subusers.map((u, index) => (
										<tr key={ u?.id || index }>
											<td className="ellipsis">{ this.fullName(u) }</td>
											<td className="ellipsis email">{ u?.email || 'Sin email' }</td>
											<td className="reg">
												{ u?.created_at ? moment(u.created_at).format('DD/MM/YYYY') : '—' }
											</td>
											<td className="text-center">
												{
													u?.has_patrimony ? (
														<span className="badge-yes">Sí</span>
													) : (
														<span className="badge-no">No</span>
													)
												}
											</td>
											<td className="text-center">
												{
													u?.has_active_plan ? (
														<span className="badge-premium">{ u?.plan_name || 'Premium' }</span>
													) : (
														<span className="badge-free">Free</span>
													)
												}
											</td>
											{ !filtered && (
												<td className="ellipsis parent">{ this.parentLabel(u) }</td>
											) }
										</tr>
									))
								}
								{
									!loading && !error && subusers.length === 0 && (
										<tr>
											<td colSpan={ colCount } className="no-items">No se encontraron subusuarios</td>
										</tr>
									)
								}
							</tbody>
						</table>
					</div>

					<div className="msd-footer">
						<Button className="btn-close-msd" color="secondary" onClick={ onClose }>
							Cerrar
						</Button>
						<Button
							className="btn-export-msd"
							disabled={ loading || subusers.length === 0 }
							onClick={ this.exportCSV }>
							Exportar a CSV
						</Button>
					</div>
				</div>
			</Modal>
		)
	}
}

export default ModalSubusersDetail;
